import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit3, AlertTriangle, Search } from "lucide-react";
import { norm } from "@/lib/normalize";
import { useLookups } from "@/context/LookupContext";
import { formatINR } from "@/lib/currency";

const empty = { name: "", category: "", sku: "", baseUnit: "", productId: "", quantity: 0, unit_price: 0, reorderLevel: 5 };

export default function Inventory() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productInfoOpen, setProductInfoOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const { lookups } = useLookups();
  const CATEGORIES = (lookups?.productCategories || []).map((c) => c.name || c);
  const isAdmin = norm(user.roleName) === "admin";

  const load = async () => {
    try {
      const res = await api.get("/inventory");
      const data = res.data;
      if (Array.isArray(data)) setRows(data);
      else if (data && Array.isArray(data.content)) setRows(data.content);
      else if (data && Array.isArray(data.items)) setRows(data.items);
      else if (data && Array.isArray(data.data)) setRows(data.data);
      else setRows([]);
    } catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, []);

  const brands = (lookups?.productBrands || []).map((b) => b.name || b);

  const filteredRows = useMemo(() => {
    return (rows || []).filter((i) => {
      const name = (i.productName || i.name || "").toLowerCase();
      const sku = (i.sku || "").toLowerCase();
      const search = searchText.toLowerCase();
      if (search && !name.includes(search) && !sku.includes(search)) return false;
      if (filterCategory && filterCategory !== "all" && (i.category || "") !== filterCategory) return false;
      if (filterBrand && filterBrand !== "all" && (i.brand || "") !== filterBrand) return false;
      if (filterStatus && filterStatus !== "all") {
        if (filterStatus === "low" && i.availableQuantity > i.reorderLevel) return false;
        if (filterStatus === "instock" && i.availableQuantity <= i.reorderLevel) return false;
      }
      return true;
    });
  }, [rows, searchText, filterCategory, filterBrand, filterStatus]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, quantity: Number(form.quantity), unit_price: Number(form.unit_price), reorderLevel: Number(form.reorderLevel) };
      if (editId) await api.put(`/inventory/${editId}`, payload);
      else await api.post("/inventory", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete item?")) return;
    try { await api.delete(`/inventory/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const openProductInfo = (item) => {
    setSelectedProduct(item);
    setProductInfoOpen(true);
  };

  return (
    <div className="space-y-8" data-testid="inventory-page">
      <div className="flex items-end justify-between gap-4">
        <div><div className="overline mb-2">Stock</div><h1 className="font-heading text-4xl text-white">Inventory</h1></div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setForm(empty); setEditId(null); }} data-testid="add-inventory-btn" className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add Item</button>
            </DialogTrigger>
            <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Edit" : "New"} item</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                  <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                </div>
                <div><Label>Base Unit</Label><Input value={form.baseUnit} disabled className="bg-emerald-elev border-gold-subtle mt-1 text-emerald-muted" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Quantity</Label><Input required type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                  <div><Label>Unit Price (₹)</Label><Input required type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                  <div><Label>Reorder at</Label><Input required type="number" value={form.reorderLevel} onChange={(e) => setForm({ ...form, reorderLevel: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                </div>
                <button type="submit" className="gold-button w-full h-11 rounded-md">Save</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Product Info Dialog (Read Only) */}
      <Dialog open={productInfoOpen} onOpenChange={setProductInfoOpen}>
        <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Product Information</DialogTitle></DialogHeader>
          {selectedProduct && (
            <div className="space-y-6">
              <div>
                <div className="overline mb-3">Product Information</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">Product Name</Label>
                    <p className="text-white mt-1">{selectedProduct.productName || selectedProduct.name}</p>
                  </div>
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">SKU</Label>
                    <p className="text-white mt-1">{selectedProduct.sku || "—"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">Category</Label>
                    <p className="text-white mt-1">{selectedProduct.category || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">Base Unit</Label>
                    <p className="text-white mt-1">{selectedProduct.baseUnit || "—"}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gold-subtle pt-4">
                <div className="overline mb-3">Stock Summary</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">Available Quantity</Label>
                    <p className="text-white mt-1">{selectedProduct.availableQuantity ?? "—"} {selectedProduct.baseUnit || ""}</p>
                  </div>
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">Reorder Level</Label>
                    <p className="text-white mt-1">{selectedProduct.reorderLevel ?? "—"} {selectedProduct.baseUnit || ""}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">Status</Label>
                    <p className="mt-1">
                      {selectedProduct.availableQuantity <= selectedProduct.reorderLevel
                        ? <Badge variant="outline" className="border-red-900 text-red-300 bg-[rgba(211,47,47,0.15)] gap-1"><AlertTriangle className="w-3 h-3" /> Low Stock</Badge>
                        : <Badge variant="outline" className="border-green-800 text-green-300 bg-[rgba(46,125,50,0.15)]">In Stock</Badge>}
                    </p>
                  </div>
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">Stock Value</Label>
                    <p className="text-gold mt-1">{selectedProduct.stockValue != null ? formatINR(selectedProduct.stockValue) : "—"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">Last Restocked</Label>
                    <p className="text-white mt-1">{selectedProduct.lastRestockedAt || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-emerald-muted text-xs uppercase tracking-widest">Last Updated</Label>
                    <p className="text-white mt-1">{selectedProduct.lastUpdatedAt || "—"}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-muted" />
          <Input
            placeholder="Search by name/SKU"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9 bg-emerald-surface border-gold-subtle text-white h-10"
            data-testid="inventory-search"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterBrand} onValueChange={setFilterBrand}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10">
            <SelectValue placeholder="All Brands" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {brands.map((b) => (
              <SelectItem key={b} value={b}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="instock">In Stock</SelectItem>
            <SelectItem value="low">Low Stock</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-gold-subtle hover:bg-transparent">
              <TableHead className="text-emerald-muted">SKU</TableHead>
              <TableHead className="text-emerald-muted">Product</TableHead>
              <TableHead className="text-emerald-muted text-right">Qty</TableHead>
              <TableHead className="text-emerald-muted text-right">Reorder</TableHead>
              <TableHead className="text-emerald-muted">Status</TableHead>
              {isAdmin && <TableHead className="text-emerald-muted w-24"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.map((i) => {
              const low = i.availableQuantity <= i.reorderLevel;
              return (
                <TableRow key={i.id} className="border-gold-subtle" data-testid={`inventory-row-${i.id}`}>
                  <TableCell className="text-emerald-muted">{i.sku}</TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => openProductInfo(i)}
                      className="text-gold hover:underline text-left"
                    >
                      {i.productName || i.name}
                    </button>
                  </TableCell>
                  <TableCell className="text-right text-white">{i.availableQuantity}{i.baseUnit ? ` (${i.baseUnit})` : ""}</TableCell>
                  <TableCell className="text-right text-emerald-muted">{i.reorderLevel}</TableCell>
                  <TableCell>
                    {low
                      ? <Badge variant="outline" className="border-red-900 text-red-300 bg-[rgba(211,47,47,0.15)] gap-1"><AlertTriangle className="w-3 h-3" /> Low</Badge>
                      : <Badge variant="outline" className="border-green-800 text-green-300 bg-[rgba(46,125,50,0.15)]">In stock</Badge>}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <button onClick={() => { setForm({ name: i.productName || i.name, category: i.category || "", sku: i.sku || "", baseUnit: i.baseUnit || "", productId: i.productId || "", quantity: i.availableQuantity || 0, unit_price: i.unit_price || 0, reorderLevel: i.reorderLevel || 0 }); setEditId(i.id); setOpen(true); }} className="text-emerald-muted hover:text-gold p-1"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => del(i.id)} className="text-emerald-muted hover:text-red-300 p-1 ml-2" data-testid={`delete-inventory-${i.id}`}><Trash2 className="w-4 h-4" /></button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
